import { motion } from 'framer-motion';
import { useHistory } from 'react-router-dom';

import {
  Flex,
  Text,
  Button,
  Badge,
  useToken,
  VStack,
  useBreakpointValue,
  readableColor,
  rem,
  TextProps,
} from '@vimeo/bokeh';
import { PlayFilled } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';

import { withResponsiveValue } from '../../hocs/withResponsiveValue';
import { getAlignmentValue, getIsActiveState } from '../../utils/helper';
import { commonWrapperLeft, commonWrapperWidth, getBorderStyles } from '../../utils/style';
import { BannerAlignment, ResponsiveValue, ShowcaseBannerProps } from '../../utils/types';
import { ArchivedTimestamp } from '../ArchivedTimestamp';
import { EllipsisTextWithShowMore } from '../EllipsisTextWithShowMore';
import { FeaturedThumbnail } from '../FeaturedThumbnail';
import { HoverState } from '../HoverState';
import { StartingTimestamp } from '../StartingTimestamp';

const ShowcaseBannerComponent = ({
  description,
  isClipView,
  isLive = false,
  isShowcaseEmpty,
  isShowcasePreview,
  isStreaming = false,
  isUpcoming = false,
  mainTitle = 'New showcase', // Default value
  topTitle = '',
  startTime = '',
  archivedTime = '',
  thumbnailUrl,
  onWatchClick,
  isMobilePreview,
  getResponsiveValue,
  showVideoDetails,
  bannerHeight,
  showLinksInDescription,
  showBannerDescription = true,
  showBannerTitle = true,
  showStartWatchingButton = true,
  textCtaAlignment = 'left',
  showcaseId,
}: ShowcaseBannerProps & ResponsiveValue) => {
  const textColor = thumbnailUrl ? 'white' : 'text-primary'; // If background image is present, always use white text
  const moreColor = thumbnailUrl ? '#B6C0CC' : 'text-secondary';

  const accentColor = useToken('colors', 'accent');
  const alignmentValue = getAlignmentValue(textCtaAlignment);
  const height = getResponsiveValue(
    isShowcaseEmpty && !thumbnailUrl
      ? { base: '220px', sm: '294px' }
      : { base: '320px', sm: '480px' },
  );
  const maxDescLength = useBreakpointValue({ base: 150, md: 200 });

  const lineHeight = useBreakpointValue({ xl: '52px', '2xl': '68px' });

  const mainTitleAs = isShowcasePreview || Boolean(topTitle) || isClipView ? undefined : 'h1';
  const showEventBadge = isLive && !archivedTime && showVideoDetails;

  const isTitleVisible = showBannerTitle && mainTitle;
  const isDescriptionVisible = showBannerDescription && description;
  const isButtonVisible = !isShowcaseEmpty && showStartWatchingButton;

  const isBadgeVisible =
    showEventBadge && (isTitleVisible || isDescriptionVisible || isButtonVisible);
  const history = useHistory();
  const isActiveState = getIsActiveState(history?.location?.pathname, 'featured');

  return (
    <Flex
      as={motion.div}
      key="banner"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      direction="column"
      align={alignmentValue}
    >
      <FeaturedThumbnail
        alignment={textCtaAlignment as BannerAlignment}
        isMobilePreview={isMobilePreview}
        isShowcaseEmpty={isShowcaseEmpty}
        thumbnail={thumbnailUrl}
        bannerHeight={bannerHeight}
      />
      <Flex
        direction="column"
        position="relative"
        minH={height}
        height={isShowcaseEmpty && !thumbnailUrl ? height : (bannerHeight ?? '50vh')}
        width="100%"
        justifyContent="flex-end"
      >
        <Flex
          px={getResponsiveValue({ base: 'md', xl: 'lg' })}
          py={
            isShowcasePreview
              ? undefined
              : getResponsiveValue({ base: 'md', md: 'xl', xl: '2xl', '2xl': '3xl' })
          }
          pb={isShowcasePreview ? 'sm' : undefined}
          direction="column"
          gap={getResponsiveValue({ base: 'md', xl: 'lg' })}
          justifyContent="end"
          align={alignmentValue}
          position="relative"
          w={getResponsiveValue(commonWrapperWidth)}
          left={getResponsiveValue(commonWrapperLeft)}
          {...getBorderStyles(isShowcasePreview, isActiveState, 'banner')}
        >
          <HoverState showcaseId={showcaseId} type="banner" />
          <Flex maxW="48rem">
            <VStack
              align="flex-start"
              gap={getResponsiveValue({ base: 'sm', md: 'md' })}
              alignItems={alignmentValue}
            >
              {isBadgeVisible ? (
                <Flex gap="sm">
                  <Badge size="sm" variant={isStreaming ? 'mature' : 'neutral'}>
                    {isStreaming ? translate('Live') : translate('Upcoming')}
                  </Badge>
                </Flex>
              ) : null}
              {isTitleVisible && (
                <Text
                  wordBreak="break-word"
                  as={mainTitleAs}
                  width="100%"
                  noOfLines={isShowcaseEmpty ? 2 : 3}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  textAlign={textCtaAlignment as TextProps['textAlign']}
                  color={textColor}
                  variant={getResponsiveValue({
                    base: 'heading-md',
                    sm: 'heading-lg',
                    md: 'heading-xl',
                    xl: 'heading-2xl',
                    '2xl': 'heading-3xl',
                  })}
                  sx={
                    !isMobilePreview
                      ? {
                          lineHeight: `${lineHeight} !important`, // Override bokeh line height
                        }
                      : undefined
                  }
                >
                  {mainTitle}
                </Text>
              )}

              {isLive && isUpcoming && startTime && !isStreaming && (
                <StartingTimestamp startTime={startTime || ''} textColor={textColor} />
              )}
              {isLive && archivedTime && (
                <ArchivedTimestamp archivedTime={archivedTime} textColor={textColor} />
              )}
              {isDescriptionVisible && (
                <Text
                  maxW={rem(640)}
                  color={textColor}
                  textAlign={textCtaAlignment as TextProps['textAlign']}
                  variant={getResponsiveValue({ base: 'body-md', xl: 'body-lg' })}
                >
                  <EllipsisTextWithShowMore
                    color={moreColor}
                    maxLength={maxDescLength}
                    modalHeader={mainTitle}
                    text={description}
                    showLinksInDescription={showLinksInDescription}
                  />
                </Text>
              )}
            </VStack>
          </Flex>
          {isButtonVisible ? (
            <Button
              width={getResponsiveValue({ base: '100%', md: 'fit-content' })}
              display="flex"
              size="md"
              color={accentColor.startsWith('#') ? readableColor(accentColor) : undefined}
              leftIcon={<PlayFilled />}
              variant={accentColor.startsWith('#') ? 'accent' : 'primary'}
              onClick={onWatchClick}
            >
              {isLive && isStreaming ? translate('Watch live') : translate('Start watching')}
            </Button>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  );
};

export const ShowcaseBanner = withResponsiveValue<ShowcaseBannerProps>(ShowcaseBannerComponent);
